import { useRef, useState } from "react";
import {
  fetchBackupUserListLoad,
  fetchRequestWormDirectory,
} from "../../api/admin";
import { fetchLoadClientLogin } from "../../api/auth";
import { BackupFolderContainer } from "../../style/contents/backup/backup";
import type { BackupUser } from "../../view/contents/backup/backup.user";
import type { DeviceItem } from "../../view/contents/backup/backup.device.list";
import ModalCenterLayout from "../modal/modal.center.layout";

interface Props {
  device: DeviceItem;
  directoryPath: string;
  onClose: () => void;
}

const DeviceWormModal = ({ device, directoryPath, onClose }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const pending = useRef(false);
  const close = () => {
    if (!pending.current) onClose();
  };

  const requestWorm = async () => {
    if (pending.current) return;
    if (!device.userId || !device.deviceId || !directoryPath) {
      alert("사용자, 디바이스 또는 폴더 정보가 없습니다.");
      return;
    }
    pending.current = true;
    setSubmitting(true);
    try {
      const companyCode = localStorage.getItem("companyCode") || "";
      const response = await fetchBackupUserListLoad(
        Number(localStorage.getItem("userId")),
        companyCode,
      );
      const user = (response.userList as BackupUser[]).find(
        (item) => item.userId === device.userId,
      );
      if (!user) throw new Error("사용자 정보를 찾을 수 없습니다.");
      const login = await fetchLoadClientLogin({
        companyCode,
        userAccount: user.userAccount,
        password: user.password,
        deviceId: "",
      });
      if (!login.jwtToken) throw new Error("로그인 토큰 정보가 없습니다.");
      await fetchRequestWormDirectory(
        String(device.userId),
        {
          deviceId: device.deviceId,
          directoryPath,
        },
        login.jwtToken,
      );
      alert("WORM 요청이 접수되었습니다.");
      onClose();
    } catch (err) {
      console.error("WORM 디렉토리 요청 실패:", err);
      alert("WORM 요청 중 오류가 발생했습니다.");
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  };

  return (
    <BackupFolderContainer>
      <ModalCenterLayout isVisible closedHandler={close}>
        <div
          className="worm-confirm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="device-worm-title"
          aria-busy={submitting}
        >
          <div className="modal-header">
            <h3 id="device-worm-title">WORM 기능 적용</h3>
            <button
              type="button"
              className="closed-modal-btn"
              aria-label="닫기"
              disabled={submitting}
              onClick={close}
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            <p>
              적용하시겠습니까? <br />
              worm 기능이 적용되면 이미 저장된 데이터의 수정/삭제가 불가능하게
              됩니다.
            </p>
            <div className="delete-target">
              <p>
                디바이스 정보 : {device.pcName + `(` + device.deviceId + `)`}
              </p>
              <p>경로 : {directoryPath}</p>
            </div>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="cancel"
              disabled={submitting}
              onClick={close}
            >
              취소
            </button>
            <button
              type="button"
              className="confirm"
              disabled={submitting}
              onClick={requestWorm}
            >
              {submitting ? "요청 중..." : "적용"}
            </button>
          </div>
        </div>
      </ModalCenterLayout>
    </BackupFolderContainer>
  );
};

export default DeviceWormModal;
